"use client";

import {
  Button,
  Card,
  Image,
  Link,
  Text,
  Dialog,
  Portal,
  CloseButton,
} from "@chakra-ui/react";
import { useRouter } from "next/navigation";
import { memo, useCallback, useEffect } from "react";
import pluralize from "pluralize";

import { ProductDetails } from "helpers/fakeData";
import ProductInfo from "@features/marketplace/catalog/ProductInfo";
import useCloseModal from "hooks/useCloseModal";
import { useStores } from "stores/rootStore";
import { observer } from "mobx-react-lite";
import { ModalType } from "stores/pageStore";

const getProductPath = (product: ProductDetails) => {
  return `/marketplace/${product.id}`;
};

interface Props {
  product: ProductDetails;
  hideAddToCart?: boolean;
}

const ColorInfo = ({ product }: Props) => {
  const colorCount = product.colors?.length ?? 0;
  if (colorCount < 2) {
    return (
      <Text fontSize="xs" color="text.secondary">
        1 color
      </Text>
    );
  }
  return (
    <Text fontSize="xs" color="text.secondary">
      {`${colorCount} ${pluralize("color", colorCount)}`}
    </Text>
  );
};

const CatalogItem = ({ product, hideAddToCart }: Props) => {
  const router = useRouter();
  const { pageStore, marketPlaceStore } = useStores();

  const onClickPreOrder = useCallback(() => {
    pageStore.openModal(ModalType.Product);
    marketPlaceStore.setSelectedProductId(product.id);
  }, [pageStore, marketPlaceStore, product.id]);

  const onClick = useCallback(() => {
    const path = getProductPath(product);
    if (path != null) {
      router.push(path);
    }
  }, [product, router]);

  useEffect(() => {
    const path = getProductPath(product);
    if (path != null) {
      router.prefetch(path);
    }
  }, [product, router]);

  return (
    <Card.Root
      overflow="hidden"
      bg="surface.background"
      borderColor="border.light"
      h="fit-content"
    >
      <Card.Body gap="2">
        <Image
          cursor="pointer"
          onClick={onClick}
          h="2xs"
          src={product.defaultImage}
          alt={product.title}
          fit="contain"
        />
        <Card.Title>
          <Link variant="underline" onClick={onClick}>
            <Text
              lineClamp={1}
              fontSize={{ base: "xs", md: "sm" }}
              fontWeight="medium"
            >
              {product.title}
            </Text>
          </Link>
          <ColorInfo product={product} />
        </Card.Title>
        <Text textStyle="2xl" fontWeight="medium" letterSpacing="tight" mt="2">
          {product.currency}
          {product.price}
        </Text>
      </Card.Body>
      {!hideAddToCart && (
        <Card.Footer gap="2">
          <Button size="xs" variant="solid" onClick={onClickPreOrder}>
            Pre-order
          </Button>
        </Card.Footer>
      )}
    </Card.Root>
  );
};

export default observer(CatalogItem);
